import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { musicVideoGradeFilter } from 'fym-shared/src/music24/musicVideoLook';
import { TypedMemo } from '../../common/types';
import { readFootageDragData, isFootageDrag } from '../footage/footageDrag';
import { replaceClip, clipNumber } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
import { PreviewEffects } from './PreviewEffects';
import { motionStyle, previewFootageMotion } from './previewEffectsMath';
import { PreviewOverlays } from './PreviewOverlays';
import { PreviewTag } from './PreviewTag';
import { useDualVideo } from './useDualVideo';
import styles from './PreviewFrame.module.scss';

interface PreviewFrameProps {
  /** `fill`: the frame takes the whole width (full screen, phone). */
  fill?: boolean;
  showSafe?: boolean;
  /** A drop of a clip on the frame replaces the selected clip (design c4). Off in full screen. */
  acceptsDrop?: boolean;
}

/**
 * One 16:9 frame of the live preview (spec §4.6): the two muted videos of the cut under the grade
 * of the look, with the effects of the cut and every overlay. The master audio is the clock; no frame
 * is made on the server.
 */
const PreviewFrameComponent: React.FC<PreviewFrameProps> = ({
  fill = false,
  showSafe = false,
  acceptsDrop = false,
}) => {
  const intl = useIntl();
  const { doc, footage, transport, selection, change, readOnly, songMap } = useMusicVideoEditor();
  const limitsFor = useClipLimits();
  const videoA = useRef<HTMLVideoElement>(null);
  const videoB = useRef<HTMLVideoElement>(null);
  const videos = useMemo(() => [videoA, videoB] as const, []);
  const view = useDualVideo({ videos, doc, lookup: footage.lookup, transport });
  const [isOver, setIsOver] = useState(false);
  const clipsById = useMemo(() => new Map(doc.clips.map((clip) => [clip.id, clip])), [doc.clips]);
  const activeClip = clipsById.get(view.slots[view.active] ?? '') ?? null;
  const activeFootage = activeClip === null ? null : footage.lookup(activeClip.source);
  const posterUrl = activeFootage?.posterUrl ?? null;
  const selectedClipId = selection?.kind === 'clip' ? selection.id : null;
  const canDrop = acceptsDrop && !readOnly;
  // The effects that move the footage (whip, zoom, shake, colour split) move the graded picture.
  const motion = useMemo(
    () => motionStyle(previewFootageMotion(doc.effects, transport.positionSec, songMap.beats)),
    [doc.effects, songMap.beats, transport.positionSec]
  );
  const gradedFilter = useMemo(
    () =>
      [musicVideoGradeFilter(doc.look.grade), motion.filter].filter((part) => part !== '' && part !== 'none').join(' '),
    [doc.look.grade, motion.filter]
  );

  const onDrop = (event: React.DragEvent) => {
    setIsOver(false);
    const data = readFootageDragData(event.dataTransfer);
    if (data === null || selectedClipId === null) {
      return;
    }
    event.preventDefault();
    change((current) => replaceClip(current, selectedClipId, data.source, limitsFor(data)));
  };

  return (
    <div
      className={clsx(styles.frame, fill && styles.frameFill, isOver && styles.frameDrop)}
      role="img"
      aria-label={intl.formatMessage(editorMessages.previewLabel)}
      onDragOver={
        canDrop
          ? (event) => {
              if (isFootageDrag(event.dataTransfer)) {
                event.preventDefault();
                setIsOver(true);
              }
            }
          : undefined
      }
      onDragLeave={canDrop ? () => setIsOver(false) : undefined}
      onDrop={canDrop ? onDrop : undefined}
    >
      <div
        className={styles.graded}
        style={{ filter: gradedFilter === '' ? 'none' : gradedFilter, transform: motion.transform }}
      >
        {posterUrl !== null && (
          // The poster holds the frame until the video plays; with "Fit with blur" it is the
          // blurred copy behind the whole clip (design c3).
          <div
            className={activeClip?.fit === 'blur' ? styles.blurBack : styles.poster}
            style={{
              backgroundImage: `url("${posterUrl}")`,
              backgroundPosition:
                activeClip === null || activeClip.fit === 'blur'
                  ? undefined
                  : `${activeClip.cropX * 100}% ${activeClip.cropY * 100}%`,
            }}
          />
        )}
        {videos.map((ref, index) => {
          const clip = clipsById.get(view.slots[index] ?? '') ?? null;
          return (
            <video
              key={index === 0 ? 'a' : 'b'}
              ref={ref}
              className={clsx(
                styles.video,
                index === view.active && styles.videoOn,
                clip?.fit === 'blur' && styles.videoContain
              )}
              style={
                clip === null || clip.fit === 'blur'
                  ? undefined
                  : { objectPosition: `${clip.cropX * 100}% ${clip.cropY * 100}%` }
              }
              muted
              playsInline
              preload="auto"
              aria-hidden
              // An expired link (one hour) fails the load: the editor reads the links again.
              onError={footage.refreshLinks}
            />
          );
        })}
      </div>
      <PreviewEffects sec={transport.positionSec} layer="picture" />
      <PreviewOverlays sec={transport.positionSec} showSafe={showSafe} />
      <PreviewEffects sec={transport.positionSec} layer="text" />
      {!fill && <PreviewTag clip={activeClip} footage={activeFootage} />}
      {isOver && (
        <div className={styles.dropNote}>
          {selectedClipId === null
            ? intl.formatMessage(editorMessages.dropNoSelection)
            : intl.formatMessage(editorMessages.dropReplace, { number: clipNumber(doc, selectedClipId) ?? 0 })}
        </div>
      )}
    </div>
  );
};

export const PreviewFrame = TypedMemo(PreviewFrameComponent);
